/**
 * The user menu's sections (#14657, ADR-0107).
 *
 * The avatar dropdown has two kinds of entry:
 *
 *   - PINNED: the identity header, the org switcher (multi-org only),
 *     Profile, Personal Settings, Admin Settings (admins only), the dev-only
 *     Component Tests link and Log Out. They are the menu's reason to exist —
 *     the only route to sign-out, the admin area and the org switcher — so
 *     they have NO key. A stored layout naming one fails validation as an
 *     unknown key, which is what makes "cannot be removed or moved"
 *     structural.
 *   - CONFIGURABLE: everything between Admin Settings and the dev section.
 *     An org arranges these into titled (or, for the default's two unheaded
 *     groups, untitled) sections, stored in `Organization.userMenuSections`.
 *
 * Since #15689 a section's entries come from the ONE entry vocabulary
 * (`ENTRY_KEYS`, `./vocabulary`) the sidebar uses too: any entry may be
 * placed here, and placing it here never removes it from the sidebar.
 *
 * Being LISTED is not being AVAILABLE: `resolveUserMenuLayout` (`./resolve`)
 * renders a listed entry only when its gate passes; placing an entry never
 * grants access.
 *
 * CLIENT-SAFE: no `server-only`, no Prisma.
 */

import { z } from 'zod'

import {
  ENTRIES,
  ENTRY_KEYS,
  entryKeySchema,
  type EntryKey,
} from './vocabulary'

/** The most sections a user menu may be split into. */
export const MAX_USER_MENU_SECTIONS = 8

/** A section heading's length bound, after trimming. */
export const MAX_USER_MENU_SECTION_TITLE_LENGTH = 40

/**
 * A section heading. `null` is a section with no heading — the default
 * layout's first and last groups have none — and a set title is 1–40
 * characters once trimmed.
 */
export const userMenuSectionTitleSchema = z
  .string()
  .trim()
  .min(1, 'A section title cannot be empty')
  .max(
    MAX_USER_MENU_SECTION_TITLE_LENGTH,
    `A section title must be ${MAX_USER_MENU_SECTION_TITLE_LENGTH} characters or less`,
  )
  .nullable()

/** The write-side validator for `updateOrgUserMenu`. */
export const userMenuLayoutSchema = z
  .object({
    sections: z
      .array(
        z
          .object({
            title: userMenuSectionTitleSchema,
            items: z.array(entryKeySchema).max(ENTRY_KEYS.length),
          })
          .strict(),
      )
      .max(
        MAX_USER_MENU_SECTIONS,
        `The user menu can have at most ${MAX_USER_MENU_SECTIONS} sections`,
      ),
  })
  .strict()
  .superRefine((layout, ctx) => {
    const seen = new Set<EntryKey>()
    for (const section of layout.sections) {
      for (const key of section.items) {
        if (seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            message: `${ENTRIES[key].label} is placed more than once`,
          })
          return
        }
        seen.add(key)
      }
    }
  })

export interface UserMenuSection {
  title: string | null
  items: EntryKey[]
}

export interface UserMenuLayout {
  sections: UserMenuSection[]
}

/**
 * The JSON stored in `userMenuSections` for a layout (#16932) — the one
 * place that shape is built. Every writer goes through it: the org editor
 * (`userMenuWriteData`), the experience-preset apply plan and the Northgate
 * seed's heal. Client-safe so the tsx-run seed can import it; the
 * `Prisma.DbNull` half for a null layout stays in `userMenuWriteData`.
 * Copies each section so the stored value never aliases the input.
 */
export function userMenuSectionsJson(layout: UserMenuLayout): {
  sections: { title: string | null; items: EntryKey[] }[]
} {
  return {
    sections: layout.sections.map(section => ({
      title: section.title,
      items: [...section.items],
    })),
  }
}
